import { SeasonGroup, TechniqueFull, WaterbodyDetail } from '@omniafishing/core';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef } from 'react';
import useDynamicRefs from '../../../../hooks/use_dynamic_refs';
import { useListAttributionUuid } from '../../../../hooks/use_list_attribution_uuid';
import { useWaterbodyRecommendations } from '../../../../hooks/use_waterbody_recommendations';
import { LINE_ATTRIBUTION } from '../../../../redux/cart';
import { ProductCarousel } from '../../../carousel/product_carousel';
import Loading from '../../../loading/loading';
import { ProductsGroupCard } from '../../../products_group_card/products_group_card';
import {
  baitCategoryNames,
  lineTerminalCategoryNames,
  rodsReelsCategoryNames,
} from '../../../waterbody_detail/waterbody_detail_lib/get_top_products';

interface TabHotbaitsProps {
  waterbody: WaterbodyDetail;
  waterbodyTechniques: TechniqueFull[];
  selectedSpeciesName: string;
  selectedTechniqueName: string;
  selectedSeasonGroup: SeasonGroup | null;
  onTechniqueClick: (techniqueName: string) => void;
}

export const TabTechniques = (props: TabHotbaitsProps) => {
  const {
    waterbody,
    waterbodyTechniques,
    selectedSpeciesName,
    selectedTechniqueName,
    onTechniqueClick,
    selectedSeasonGroup,
  } = props;

  const { getRef, setRef } = useDynamicRefs();
  const techniquesWrapperRef = useRef<HTMLOListElement>();

  const { recommendations, isLoading } = useWaterbodyRecommendations({
    waterbodySlug: waterbody.url_slug,
    speciesName: selectedSpeciesName,
    seasonGroupName: selectedSeasonGroup?.name,
    techniqueName: selectedTechniqueName,
  });

  const baitRecommendations = useMemo(() => {
    return recommendations.filter((r) => baitCategoryNames.includes(r.product_family.category));
  }, [recommendations]);
  const lineTerminalRecommendations = useMemo(() => {
    return recommendations.filter((r) =>
      lineTerminalCategoryNames.includes(r.product_family.category)
    );
  }, [recommendations]);
  const rodsReelsRecommendations = useMemo(() => {
    return recommendations.filter((r) =>
      rodsReelsCategoryNames.includes(r.product_family.category)
    );
  }, [recommendations]);

  const list_uuid = useListAttributionUuid();

  useEffect(() => {
    if (selectedTechniqueName) {
      const selectedTechniqueEl = getRef<HTMLLIElement>(selectedTechniqueName).current;
      const wrapperEl = techniquesWrapperRef.current;
      if (wrapperEl && selectedTechniqueEl) {
        const wrapperRect = wrapperEl.getBoundingClientRect();
        const selectedTechniqueRect = selectedTechniqueEl.getBoundingClientRect();
        const relativeLeft = selectedTechniqueRect.left - wrapperRect.left;
        wrapperEl.scrollTo({
          left: relativeLeft + wrapperEl.scrollLeft - 12,
          behavior: 'smooth',
        });
      }
    }
  }, [selectedTechniqueName]);

  return (
    <div>
      <section>
        <section className="mb-6 pt-6">
          <div className="px-3 mb-9">
            <p className={'mb-2 font-bold text-xl'}>Top 3 Techniques</p>
            <ol
              className="flex gap-3 w-[calc(100%+24px)] overflow-auto -ml-3 px-3"
              ref={techniquesWrapperRef}
            >
              {waterbodyTechniques.slice(0, 3).map((t) => {
                return (
                  <li
                    key={t.name}
                    className={`w-[36%] sm:w-[31.5%] flex-grow-0 flex-shrink-0 cursor-pointer`}
                    ref={setRef(t.name)}
                    onClick={() => {
                      onTechniqueClick(t.name);
                    }}
                  >
                    <div
                      className={`p-3 rounded-lg border flex flex-col gap-1 items-center text-center font-medium h-full bg-white ${
                        selectedTechniqueName === t.name && 'border-black'
                      }`}
                    >
                      <div>
                        <img src={t.image} alt={t.img_alt || ''} width={50} />
                      </div>
                      <span className="mt-auto">{t.display_name}</span>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
          {isLoading ? (
            <div className="px-3">
              <Loading text="Loading Recommended Products" />
            </div>
          ) : (
            <>
              {baitRecommendations.length > 0 && (
                <div className="mb-9">
                  <p className="pl-3 mb-2 font-bold text-xl">
                    Top Baits ({baitRecommendations.length})
                  </p>
                  <ProductCarousel
                    adaptive
                    products={_.flatten(baitRecommendations.map((rf) => rf.products))}
                    arrows={true}
                    list_id_title={`Top Baits`}
                    list_uuid={list_uuid}
                    fadedEdges={false}
                    renderOnlyVisible={true}
                  >
                    {baitRecommendations.map((recommendedFamily, i) => {
                      return (
                        <div
                          key={recommendedFamily.product_family.handle}
                          className={'w-[calc(36%-24px)] pt-1 px-3'}
                        >
                          <ProductsGroupCard
                            products={recommendedFamily.products}
                            position={i}
                            list_uuid={list_uuid}
                            showTechniqueReported={false}
                            lineItemAttribution={[
                              {
                                key: LINE_ATTRIBUTION.WATERBODY_ID,
                                value: waterbody.id,
                              },
                            ]}
                            price_pro={recommendedFamily.product_family.price_pro}
                          />
                        </div>
                      );
                    })}
                  </ProductCarousel>
                </div>
              )}
              {lineTerminalRecommendations.length > 0 && (
                <div className="mb-9">
                  <p className="pl-3 mb-2 font-bold text-xl">
                    Top Line and Terminal ({lineTerminalRecommendations.length})
                  </p>
                  <ProductCarousel
                    adaptive
                    products={_.flatten(lineTerminalRecommendations.map((rf) => rf.products))}
                    arrows={true}
                    list_id_title={`Top Line and Terminal`}
                    list_uuid={list_uuid}
                    fadedEdges={false}
                    renderOnlyVisible={true}
                  >
                    {lineTerminalRecommendations.map((recommendedFamily, i) => {
                      return (
                        <div
                          key={recommendedFamily.product_family.handle}
                          className={'w-[calc(36%-24px)] pt-1 px-3'}
                        >
                          <ProductsGroupCard
                            products={recommendedFamily.products}
                            position={i}
                            list_uuid={list_uuid}
                            showTechniqueReported={false}
                            lineItemAttribution={[
                              {
                                key: LINE_ATTRIBUTION.WATERBODY_ID,
                                value: waterbody.id,
                              },
                            ]}
                            price_pro={recommendedFamily.product_family.price_pro}
                          />
                        </div>
                      );
                    })}
                  </ProductCarousel>
                </div>
              )}
              {rodsReelsRecommendations.length > 0 && (
                <div className="mb-9">
                  <p className="pl-3 mb-2 font-bold text-xl">
                    Top Rods and Reels ({rodsReelsRecommendations.length})
                  </p>
                  <ProductCarousel
                    adaptive
                    products={_.flatten(rodsReelsRecommendations.map((rf) => rf.products))}
                    arrows={true}
                    list_id_title={`Top Rods and Reels`}
                    list_uuid={list_uuid}
                    fadedEdges={false}
                    renderOnlyVisible={true}
                  >
                    {rodsReelsRecommendations.map((recommendedFamily, i) => {
                      return (
                        <div
                          key={recommendedFamily.product_family.handle}
                          className={'w-[calc(36%-24px)] pt-1 px-3'}
                        >
                          <ProductsGroupCard
                            products={recommendedFamily.products}
                            position={i}
                            list_uuid={list_uuid}
                            showTechniqueReported={false}
                            lineItemAttribution={[
                              {
                                key: LINE_ATTRIBUTION.WATERBODY_ID,
                                value: waterbody.id,
                              },
                            ]}
                            price_pro={recommendedFamily.product_family.price_pro}
                          />
                        </div>
                      );
                    })}
                  </ProductCarousel>
                </div>
              )}
            </>
          )}
        </section>
      </section>
    </div>
  );
};
